import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { storeProducts, storeCategories } from "@/mocks/store";

export default function StorePage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [cart, setCart] = useState<number[]>([]);
  const [addedId, setAddedId] = useState<number | null>(null);

  const filtered =
    activeCategory === "All"
      ? storeProducts
      : storeProducts.filter((p) => p.category === activeCategory);

  const handleAdd = (id: number) => {
    setCart((prev) => [...prev, id]);
    setAddedId(id);
    setTimeout(() => setAddedId(null), 1500);
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="text-xs tracking-widest uppercase text-amber-700 font-semibold">Resources</span>
          <h1
            className="text-4xl md:text-5xl font-bold text-stone-800 mt-3 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Faith &amp; Health Store
          </h1>
          <p className="text-stone-500 max-w-xl mx-auto text-base leading-relaxed">
            Books, audio sermons, health supplements, apparel, and digital resources to support your journey of healing and faith.
          </p>
          {cart.length > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-sm font-medium">
              <i className="ri-shopping-cart-line" />
              {cart.length} item{cart.length > 1 ? "s" : ""} in cart
            </div>
          )}
        </div>
      </section>

      {/* Donation Banner */}
      <section className="py-8 bg-amber-700">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-white text-center sm:text-left">
            <p className="font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Support the Ministry</p>
            <p className="text-amber-200 text-sm">Your generous donation helps us reach more lives with healing and hope.</p>
          </div>
          <a
            href="#"
            className="px-6 py-2.5 rounded-full bg-white text-amber-700 text-sm font-bold hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer"
          >
            Give a Donation
          </a>
        </div>
      </section>

      {/* Products */}
      <section className="py-16 bg-white" data-product-shop>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap mb-10">
            {storeCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-amber-700 text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filtered.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl border border-stone-100 overflow-hidden hover:border-amber-200 hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="relative w-full h-52 overflow-hidden bg-stone-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.badge && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-amber-700 text-white text-xs font-bold">
                      {product.badge}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <p className="text-xs text-stone-400 mb-1">{product.category}</p>
                  <h3 className="font-bold text-stone-800 text-sm leading-snug mb-1">{product.name}</h3>
                  <p className="text-xs text-stone-500 leading-relaxed mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-stone-800">${product.price.toFixed(2)}</span>
                    <button
                      onClick={() => handleAdd(product.id)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        addedId === product.id
                          ? "bg-green-600 text-white"
                          : "bg-stone-800 text-white hover:bg-amber-700"
                      }`}
                    >
                      {addedId === product.id ? "Added!" : "Add to Cart"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-stone-400 mt-8">
            Free shipping on orders over $50 &bull; Digital downloads delivered instantly
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
